"use client";

import { cn } from "@newxsystems/shared";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";

interface IKaajMasonryLayoutProps {
  children: ReactNode;
  className?: string;
}

const toPixelValue = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const KaajMasonryLayout = ({ children, className }: IKaajMasonryLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    if (!layout) return;

    let animationFrame = 0;
    let observedLayoutWidth = 0;
    const items = Array.from(layout.querySelectorAll<HTMLElement>("[data-kaaj-masonry-item]"));
    const updateLayout = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const styles = getComputedStyle(layout);
        const rowHeight = toPixelValue(styles.gridAutoRows);
        const rowGap = toPixelValue(styles.rowGap);
        if (rowHeight <= 0) return;

        for (const item of items) {
          const content = item.firstElementChild as HTMLElement | null;
          if (!content) continue;

          const itemStyles = getComputedStyle(item);
          const contentHeight =
            content.getBoundingClientRect().height +
            toPixelValue(itemStyles.paddingTop) +
            toPixelValue(itemStyles.paddingBottom);
          const rowSpan = Math.max(1, Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap)));
          const nextGridRowEnd = `span ${rowSpan}`;

          if (item.style.gridRowEnd !== nextGridRowEnd) {
            item.style.gridRowEnd = nextGridRowEnd;
          }
        }
      });
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const layoutEntry = entries.find((entry) => entry.target === layout);
      if (layoutEntry) {
        const nextWidth = layoutEntry.contentRect.width;
        if (nextWidth === observedLayoutWidth && entries.length === 1) return;
        observedLayoutWidth = nextWidth;
      }
      updateLayout();
    });
    resizeObserver.observe(layout);
    for (const item of items) {
      const content = item.firstElementChild;
      if (content) resizeObserver.observe(content);
    }
    updateLayout();

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <div
      className={cn(
        "relative grid grid-cols-1 items-start gap-x-5 gap-y-px [grid-auto-rows:1px] md:grid-cols-2 md:gap-x-6 xl:grid-cols-3 xl:gap-x-7",
        className,
      )}
      ref={layoutRef}
    >
      {children}
    </div>
  );
};
